
import RestauranteCheckout from "../Checkout/Index.vue";

// Mantém integralmente o Checkout fiscal do restaurante (inclusive os mesmos
// endpoints de pré-venda, NFC-e e confirmação). A única especialização é a
// conclusão logística idempotente ao voltar para o Kanban.
export default {
  name: "PedidoOnlineCheckout",
  extends: RestauranteCheckout,
  data() {
    return {
      finalizacaoOnlineEmAndamento: false,
      pedidoOnlineOperacional: null,
    };
  },
  methods: {
    unwrapPedidoOnline(payload) {
      let value = payload;
      let depth = 0;
      while (
        value &&
        !Array.isArray(value) &&
        typeof value === "object" &&
        value.data &&
        typeof value.data === "object" &&
        depth < 3
      ) {
        value = value.data;
        depth += 1;
      }
      return value?.pedido || value?.pedido_online || value || {};
    },
    pagamentoOperacionalConfirmado(pedido) {
      const context = pedido?.checkout_context || {};
      const operation = pedido?.operacao || pedido?.operational_link || {};
      const payment =
        pedido?.payment ||
        pedido?.pagamento ||
        (Array.isArray(pedido?.pagamentos) ? pedido.pagamentos[0] : {});

      return Boolean(
        pedido?.operational_payment_confirmed === true ||
          context.checkout_concluido === true ||
          context.operational_confirmed === true ||
          payment?.operationally_confirmed === true ||
          (operation.liquidado_at &&
            (operation.fechamento_id ||
              operation.nfe_id ||
              operation.pre_venda_id))
      );
    },
    async getCheckout() {
      await RestauranteCheckout.methods.getCheckout.call(this);

      const pedidoOnlineId = this.$route.params.pedidoOnlineId;
      if (!pedidoOnlineId || !this.checkout) {
        return;
      }

      try {
        const { data } = await this.$http.get(
          `/restaurante/pedidos-online/${pedidoOnlineId}`
        );
        this.pedidoOnlineOperacional = this.unwrapPedidoOnline(data);
        this.aplicarTotalImutavelDoPedido();
      } catch (error) {
        this.handleError(
          error,
          "Não foi possível conferir o total imutável do pedido digital."
        );
      }
    },
    aplicarTotalImutavelDoPedido() {
      const totalPedido = Number(this.pedidoOnlineOperacional?.total || 0);
      if (!(totalPedido > 0) || !this.cobrancaItens.length) {
        return;
      }

      const linhas = this.cobrancaItens
        .map((linha) => ({
          linha,
          item: (this.checkout?.itens || []).find(
            (item) => Number(item.id) === Number(linha.atendimento_item_id)
          ),
        }))
        .filter(({ item, linha }) => item && Number(linha.quantidade || 0) > 0);
      if (!linhas.length) {
        return;
      }

      linhas.forEach(({ linha }) => {
        linha.ajuste_operacao = null;
        linha.ajuste_tipo = 1;
        linha.ajuste_valor = 0;
        delete linha._pedido_online_ajuste;
      });

      const totalItens = linhas.reduce((total, { item, linha }) => {
        const quantidadePendente = Number(item.quantidade_pendente || 0);
        const unitario =
          quantidadePendente > 0
            ? Number(item.valor_pendente || 0) / quantidadePendente
            : Number(item.valor_unitario || 0);
        return total + unitario * Number(linha.quantidade || 0);
      }, 0);
      let diferenca = Number((totalPedido - totalItens).toFixed(2));
      if (Math.abs(diferenca) < 0.01) {
        return;
      }

      const operacao = diferenca > 0 ? "acrescimo" : "desconto";
      diferenca = Math.abs(diferenca);
      for (const { item, linha } of linhas) {
        if (diferenca < 0.01) break;
        const quantidade = Number(linha.quantidade || 0);
        const quantidadePendente = Number(item.quantidade_pendente || 0);
        const unitario =
          quantidadePendente > 0
            ? Number(item.valor_pendente || 0) / quantidadePendente
            : Number(item.valor_unitario || 0);
        const limite =
          operacao === "desconto"
            ? Math.max(unitario * quantidade - 0.01, 0)
            : diferenca;
        const parcela = Math.min(diferenca, limite);
        if (!(parcela >= 0.01) || !(quantidade > 0)) continue;

        linha.ajuste_operacao = operacao;
        linha.ajuste_tipo = 1;
        linha.ajuste_valor = Number((parcela / quantidade).toFixed(6));
        linha._pedido_online_ajuste = true;
        diferenca = Number((diferenca - parcela).toFixed(2));
      }

      if (diferenca >= 0.01) {
        this.$toast.error(
          "O total operacional não pôde ser conciliado com o pedido. A cobrança foi bloqueada."
        );
        this.cobrancaItens = [];
      }
    },
    openAjusteItemModal() {
      this.$toast.warning(
        "Itens e valores do pedido digital são imutáveis neste Checkout."
      );
    },
    updateQuantidade() {
      this.$toast.warning(
        "A quantidade do pedido digital não pode ser alterada no recebimento."
      );
    },
    removeItemCobranca() {
      this.$toast.warning(
        "Os itens do pedido digital não podem ser removidos no recebimento."
      );
    },
    ajusteItemLabel(linha) {
      if (linha?._pedido_online_ajuste) {
        const taxaEntrega = Number(
          this.pedidoOnlineOperacional?.taxa_entrega || 0
        );
        const valor =
          Number(linha.ajuste_valor || 0) * Number(linha.quantidade || 0);
        return taxaEntrega > 0 && Math.abs(valor - taxaEntrega) < 0.01
          ? `Taxa de entrega: ${this.formatMoney(valor)}`
          : `Ajuste imutável do pedido: ${this.formatMoney(valor)}`;
      }
      return RestauranteCheckout.methods.ajusteItemLabel.call(this, linha);
    },
    async confirmarPagamento() {
      const fechamentoId = this.pagamentoFechamento?.id;

      await RestauranteCheckout.methods.confirmarPagamento.call(this);

      // O Checkout padrão só volta automaticamente quando a mesa é liberada.
      // No pedido online, a confirmação fiscal bem-sucedida também precisa
      // disparar a conclusão logística (retirada ou retorno da entrega).
      if (fechamentoId && !this.pagamentoFechamento) {
        await this.voltarMesas();
      }
    },
    async voltarMesas() {
      if (this.finalizacaoOnlineEmAndamento) {
        return;
      }

      this.finalizacaoOnlineEmAndamento = true;
      const pedidoOnlineId = this.$route.params.pedidoOnlineId;
      const operacaoFinal = this.$route.query.operacao_final;
      let operacaoConcluida = false;
      let pagamentoConfirmado = false;
      let pagamentoFiscalConfirmado = false;

      if (pedidoOnlineId && operacaoFinal) {
        try {
          const { data } = await this.$http.get(
            `/restaurante/pedidos-online/${pedidoOnlineId}`
          );
          const pedido = this.unwrapPedidoOnline(data);
          pagamentoFiscalConfirmado =
            this.pagamentoOperacionalConfirmado(pedido);
          const expectedVersion = Number(
            pedido.version || this.$route.query.expected_version || 0
          );
          let concluirOperacao;

          if (
            pagamentoFiscalConfirmado &&
            operacaoFinal === "concluir_retirada"
          ) {
            concluirOperacao = () =>
              this.$http.post(
                `/restaurante/pedidos-online/${pedidoOnlineId}/retirada/concluir`,
                {
                  expected_version: expectedVersion,
                  idempotency_key: `checkout-retirada-${pedidoOnlineId}`,
                  observacao:
                    "Retirada concluída após recebimento no Checkout fiscal.",
                },
                {
                  headers: {
                    "Idempotency-Key": `checkout-retirada-${pedidoOnlineId}`,
                  },
                }
              );
          } else if (
            pagamentoFiscalConfirmado &&
            operacaoFinal === "concluir_retorno_entrega"
          ) {
            concluirOperacao = () =>
              this.$http.post(
                `/restaurante/entregas-online/pedidos/${pedidoOnlineId}/retornar`,
                {
                  expected_version: expectedVersion,
                  idempotency_key: `checkout-retorno-entrega-${pedidoOnlineId}`,
                  observacao:
                    "Pagamento recebido e retorno concluído pelo Checkout fiscal.",
                },
                {
                  headers: {
                    "Idempotency-Key": `checkout-retorno-entrega-${pedidoOnlineId}`,
                  },
                }
              );
          }

          if (concluirOperacao) {
            await concluirOperacao();

            let verificacao = await this.$http.get(
              `/restaurante/pedidos-online/${pedidoOnlineId}`
            );
            let pedidoPersistido = this.unwrapPedidoOnline(verificacao.data);
            pagamentoConfirmado =
              this.pagamentoOperacionalConfirmado(pedidoPersistido);

            if (!pagamentoConfirmado) {
              // O comando possui chave idempotente. Um retry cobre o intervalo
              // em que o fechamento terminou, mas o espelho do pedido ainda
              // não foi atualizado, sem gerar nova venda ou novo pagamento.
              await concluirOperacao();
              verificacao = await this.$http.get(
                `/restaurante/pedidos-online/${pedidoOnlineId}`
              );
              pedidoPersistido = this.unwrapPedidoOnline(verificacao.data);
              pagamentoConfirmado =
                this.pagamentoOperacionalConfirmado(pedidoPersistido);
            }

            if (!pagamentoConfirmado) {
              throw new Error(
                "A venda foi finalizada, mas o pedido ainda não confirmou o pagamento. Atualize o Kanban para reconciliar sem cobrar novamente."
              );
            }

            operacaoConcluida = true;
          }
        } catch (error) {
          pagamentoConfirmado = false;
          if (pagamentoFiscalConfirmado) {
            this.$toast.warning(
              error?.response?.data?.msg ||
                error?.message ||
                (operacaoFinal === "concluir_retorno_entrega"
                  ? "O pagamento foi confirmado, mas o retorno do entregador ainda precisa ser concluído no Kanban."
                  : "O pagamento foi confirmado, mas a retirada ainda precisa ser concluída no Kanban.")
            );
          } else {
            this.$toast.warning(
              "Não foi possível conferir o fechamento fiscal agora. O pedido permanecerá pendente no Kanban para conferência."
            );
          }
        }
      }

      try {
        await this.$router.push({
          name: "restaurantePedidosOnline",
          query: {
            view: this.$route.query.return_view || "operation",
            lane:
              this.$route.query.return_lane ||
              (operacaoFinal === "concluir_retorno_entrega"
                ? "entrega"
                : "retirada"),
            pedido:
              this.$route.query.return_pedido ||
              this.$route.params.pedidoOnlineId,
            ...(pagamentoConfirmado
              ? {
                  checkout_pedido: this.$route.params.pedidoOnlineId,
                  checkout_concluido: "1",
                }
              : {}),
            ...(operacaoConcluida && operacaoFinal === "concluir_retirada"
              ? { retirada_concluida: "1" }
              : {}),
            ...(operacaoConcluida &&
            operacaoFinal === "concluir_retorno_entrega"
              ? { retorno_entrega_concluido: "1" }
              : {}),
          },
        });
      } finally {
        this.finalizacaoOnlineEmAndamento = false;
      }
    },
  },
};
